import { useEffect, useState } from 'react';
import { cx } from '@emotion/css';
import { DSIcon } from '@stihl-design-system/components';

import { getDesignSystemIcon } from '@phoenix/design-system/frontend/util/path.utils';
import { Raffle as RaffleProps } from '@phoenix/team-m/my-tool-shed/frontend/domain/model/benefits.model';
import { Image } from '@phoenix/team-m/dashboard/frontend/shared/components/image';
import { containerQueries } from '@phoenix/design-system/frontend/ui/breakpoints/breakpoints';
import { useRaffleParticipation } from '@phoenix/team-m/my-tool-shed/frontend/shared/raffle/use-raffle-participation';

import { RafflePromo } from './raffle-promo';
import { RaffleSteps } from './raffle-steps';

export enum Steps {
  PARTICIPATION = 1,
  TERMS_CONDITIONS = 2,
  AFTER_SUBMISSION = 3,
}

export const Raffle: React.FC<RaffleProps> = ({
  id,
  headline,
  description,
  images,
  eligible,
  startDateOfPurchaseDate,
  endDateOfPurchaseDate,
  afterSubmissionResponse,
  questions,
}) => {
  const [currentStep, setCurrentStep] = useState(Steps.PARTICIPATION);
  const { mutateAsync, isLoading, isSuccess, isError } = useRaffleParticipation();
  const [participationSuccess, setParticipationSuccess] = useState<boolean | undefined>();

  const setNextStep = () => {
    setCurrentStep((prev) => prev + 1);
  };

  useEffect(() => {
    if (isSuccess || isError) {
      setNextStep();
      setParticipationSuccess(isSuccess);
    }
  }, [isSuccess, isError]);

  const needsFullSpace = !!questions?.length && currentStep === Steps.TERMS_CONDITIONS;

  return (
    <div className="relative flex flex-col-reverse tablet:flex-row bg-secondary-light tablet:min-h-[376px]">
      {eligible ? (
        <RaffleSteps
          id={id}
          headline={headline}
          description={description.plaintext}
          afterSubmissionResponse={afterSubmissionResponse.plaintext}
          questions={questions}
          currentStep={currentStep}
          setNextStep={setNextStep}
          enterRaffle={mutateAsync}
          isSubmitting={isLoading}
          participationSuccess={participationSuccess}
        />
      ) : (
        <RafflePromo periodStart={startDateOfPurchaseDate} periodEnd={endDateOfPurchaseDate} />
      )}
      <div
        className={cx(
          needsFullSpace && 'tablet:hidden',
          'absolute h-1 tablet:h-auto w-full tablet:-inset-y-7 tablet:inset-x-2/3 desktop:inset-x-1/2 tablet:w-1 z-50 bg-primary-orange top-[224px]'
        )}
      >
        <div
          className={cx(
            'absolute inset-x-0 tablet:-inset-x-7 inset-y-1/2 tablet:inset-y-3/4 desktop:inset-y-0 m-auto w-14 h-14 desktop:w-16 desktop:h-16 flex items-center justify-center bg-primary-orange rounded-full',
            eligible ? 'bg-primary-orange' : 'bg-white border-black border-2'
          )}
        >
          <DSIcon source={getDesignSystemIcon('icon-present.svg')} size="x-large" />
        </div>
      </div>
      <div
        className={cx(
          needsFullSpace && 'tablet:hidden',
          'relative w-full tablet:max-w-[33%] desktop:max-w-[50%] tablet:min-h-[376px] min-h-[224px]'
        )}
      >
        <Image
          src={(eligible ? images.main?.url : images.secondary?.url) ?? ''}
          alt=""
          aria-hidden
          sizes={`(max-width: ${containerQueries.miniTablet}) 100vw, 25vw`}
          className={cx('object-cover', { 'opacity-50': participationSuccess === false })}
          fill
        />
      </div>
    </div>
  );
};

export default Raffle;
